import React, { useState } from "react";
import { Drawer, message } from "antd5";
import { Modal, Typography } from "antd";
import ButtonItem from "./ButtonItem";
import InputItem from "./InputItem";
import moment from "moment";
import axios from "axios";
import TextAreaItem from "./TextAreaItem";
import {
  CheckSquareOutlined,
  CloseSquareOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const EstadoLeadBadge = ({ estado }) => {
  const map = {
    0: { label: "Perdido", color: "#ff4d4f" },
    1: { label: "Abierto", color: "#1890ff" },
    2: { label: "Ganado / Abierto", color: "#52c41a" },
    3: { label: "Ganado / Cerrado", color: "#389e0d" },
  };
  const { label, color } = map[estado] ?? map[1];
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 20,
        background: `${color}18`,
        color,
        border: `1px solid ${color}40`,
      }}
    >
      {label}
    </span>
  );
};

const Seccion = ({ titulo, children }) => (
  <div style={{ marginBottom: 20 }}>
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        color: "#bbb",
        marginBottom: 10,
      }}
    >
      {titulo}
    </div>
    {children}
  </div>
);

const Campo = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, color: "#aaa", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: "#1a1a1a", fontWeight: 500 }}>
        {value}
      </div>
    </div>
  );
};

const EventDetailDrawer = ({
  visible,
  evento,
  onClose,
  onEditar,
  onActualizado,
  headersToken,
  // Oculta completar / comentar / eliminar y deja solo la consulta y el botón de editar. Existe
  // porque este drawer nació para las tareas del CRM y sus tres llamadas están amarradas a esa
  // colección y a su payload; un módulo que reutilice el calendario con otra colección —las tareas
  // de habitación de Hotelería— muestra el detalle aquí y hace las acciones en su propia pantalla,
  // en vez de forzar un contrato común a martillazos.
  soloLectura = false,
}) => {
  const [loadingToggle, setLoadingToggle] = useState(false);
  const [loadingEliminar, setLoadingEliminar] = useState(false);
  const [completado, setCompletado] = useState(false);
  const [comentario, setComentario] = useState("");
  const [guardandoComentario, setGuardandoComentario] = useState(false);

  // Modal de confirmación para eliminar
  const [modalEliminarVisible, setModalEliminarVisible] = useState(false);
  const [textoConfirmacion, setTextoConfirmacion] = useState("");

  React.useEffect(() => {
    if (evento) {
      setCompletado(evento.completado ?? false);
      setComentario(evento.comentario ?? "");
    }
    setTextoConfirmacion("");
    setModalEliminarVisible(false);
  }, [evento]);

  if (!evento) return null;

  const color = evento.color || "#9b9b9b";
  const tieneFecha = evento.start && moment(evento.start).isValid();

  const horario = !tieneFecha
    ? "Sin fecha definida"
    : evento.allDay
      ? "Todo el día"
      : `${moment(evento.start).format("HH:mm")} – ${moment(evento.end).format("HH:mm")}`;

  const fecha = !tieneFecha
    ? "Sin fecha definida"
    : evento.allDay
      ? moment(evento.start).format("dddd D [de] MMMM")
      : moment(evento.start).format("dddd D [de] MMMM · HH:mm");

  const handleToggleCompletado = () => {
    setLoadingToggle(true);
    axios
      .post(
        "leads/toggleTarea",
        { tareaId: evento.id },
        { headers: headersToken },
      )
      .then((res) => {
        if (res.data?.success === 2) {
          setCompletado(res.data.completado);
          message.success(
            res.data.completado
              ? "Tarea completada"
              : "Tarea marcada como pendiente",
          );
          onActualizado?.();
        } else {
          message.error("No se pudo actualizar la tarea");
        }
      })
      .catch(() => message.error("Error de conexión"))
      .finally(() => setLoadingToggle(false));
  };

  const handleGuardarComentario = () => {
    setGuardandoComentario(true);
    axios
      .post(
        "leads/actualizarComentarioTarea",
        { tareaId: evento.id, comentario },
        { headers: headersToken },
      )
      .then((res) => {
        if (res.data?.success === 2) {
          message.success("Comentario guardado");
        } else {
          message.error("No se pudo guardar el comentario");
        }
      })
      .catch(() => message.error("Error de conexión"))
      .finally(() => setGuardandoComentario(false));
  };

  const handleEliminar = () => {
    setLoadingEliminar(true);
    axios
      .post(
        "leads/eliminarTarea",
        { tareaId: evento.id },
        { headers: headersToken },
      )
      .then((res) => {
        if (res.data?.success === 2) {
          message.success("Tarea eliminada");
          setModalEliminarVisible(false);
          onClose();
          onActualizado?.();
        } else {
          message.error("No se pudo eliminar la tarea");
        }
      })
      .catch(() => message.error("Error de conexión"))
      .finally(() => setLoadingEliminar(false));
  };

  const puedeEliminar = textoConfirmacion.trim().toLowerCase() === "eliminar";

  return (
    <>
      <Modal
        title="¿Eliminar tarea?"
        visible={modalEliminarVisible}
        onCancel={() => {
          setModalEliminarVisible(false);
          setTextoConfirmacion("");
        }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <ButtonItem
              danger
              onClick={() => {
                setModalEliminarVisible(false);
                setTextoConfirmacion("");
              }}
            >
              Cancelar
            </ButtonItem>
            <ButtonItem
              rojo
              disabled={!puedeEliminar}
              loading={loadingEliminar}
              onClick={handleEliminar}
            >
              Eliminar
            </ButtonItem>
          </div>
        }
      >
        <Text style={{ display: "block", marginBottom: 12 }}>
          Esta acción no se puede deshacer. Escribe{" "}
          <Text strong>"eliminar"</Text> para confirmar.
        </Text>
        <InputItem
          placeholder='Escribe "eliminar" para confirmar'
          value={textoConfirmacion}
          onChange={(e) => setTextoConfirmacion(e.target.value)}
          onPressEnter={() => puedeEliminar && handleEliminar()}
        />
      </Modal>
      <Drawer
        open={visible}
        onClose={onClose}
        width={400}
        styles={{ body: { padding: 0 }, header: { display: "none" } }}
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            {!soloLectura && (
            <ButtonItem
              block
              className="botonAgregar"
              verde={!completado}
              blanco={completado}
              loading={loadingToggle}
              onClick={handleToggleCompletado}
              style={{ width: "100%" }}
              icon={
                completado ? <CloseSquareOutlined /> : <CheckSquareOutlined />
              }
            >
              {completado ? "Marcar pendiente" : "Marcar completado"}
            </ButtonItem>
            )}

            <ButtonItem
              block
              negro2
              className="botonAgregar"
              onClick={() => onEditar(evento)}
              style={{ width: "100%" }}
              icon={<EditOutlined />}
            >
              Editar
            </ButtonItem>

            {!soloLectura && (
              <ButtonItem
                danger
                className="botonAgregar"
                onClick={() => setModalEliminarVisible(true)}
                icon={<DeleteOutlined />}
              />
            )}
          </div>
        }
      >
        <div
          style={{
            background: `${color}18`,
            borderBottom: `3px solid ${color}`,
            padding: "20px 20px 16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {evento.leadNombre && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: color,
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      color,
                    }}
                  >
                    {evento.leadNombre}
                  </span>
                  {evento.etapaNombre && (
                    <span style={{ fontSize: 10, color: "#aaa" }}>
                      · {evento.etapaNombre}
                    </span>
                  )}
                </div>
              )}
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#1a1a1a",
                  textDecoration: completado ? "line-through" : "none",
                  opacity: completado ? 0.5 : 1,
                }}
              >
                {evento.title}
              </div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                {fecha}
              </div>
            </div>

            {/* Botón cerrar */}
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#aaa",
                fontSize: 18,
                lineHeight: 1,
                padding: 4,
                marginLeft: 8,
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Cuerpo */}
        <div style={{ padding: "20px" }}>
          <Seccion titulo="Tarea">
            <Campo label="Horario" value={horario} />
            <Campo label="Descripción" value={evento.descripcion} />
          </Seccion>
          {!soloLectura && (
            <Seccion titulo="Comentario">
              <TextAreaItem
                placeholder="Añadir comentario..."
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 5 }}
                style={{ fontSize: 13, marginBottom: 8 }}
              />
              <ButtonItem
                negro2
                loading={guardandoComentario}
                onClick={handleGuardarComentario}
                style={{ width: "100%" }}
              >
                Guardar comentario
              </ButtonItem>
            </Seccion>
          )}

          {(evento.leadValor || evento.leadEstado !== undefined) && (
            <Seccion titulo="Lead">
              {evento.leadValor && (
                <Campo
                  label="Valor"
                  value={`$${Number(evento.leadValor).toLocaleString("es-MX")}`}
                />
              )}
              {evento.leadEstado !== undefined && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: "#aaa", marginBottom: 4 }}>
                    Estado
                  </div>
                  <EstadoLeadBadge estado={evento.leadEstado} />
                </div>
              )}
            </Seccion>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default EventDetailDrawer;
